import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { activitiesAPI, groupsAPI } from '../api/client';
import '../styles/create-activity.css';

const activityTypes = [
  { value: 'run', label: 'Run' },
  { value: 'coffee', label: 'Coffee' },
  { value: 'hike', label: 'Hike' },
  { value: 'museum', label: 'Museum' },
  { value: 'cycling', label: 'Cycling' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'other', label: 'Other' },
];

export default function EditActivity() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadData = async () => {
      try {
        const [actRes, groupsRes] = await Promise.all([
          activitiesAPI.getById(id),
          groupsAPI.getAll(),
        ]);
        const a = actRes.data;
        setGroups(groupsRes.data || []);
        setFormData({
          title: a.title || '',
          activity_type: a.activity_type || 'run',
          description: a.description || '',
          start_time: a.start_time ? a.start_time.slice(0, 16) : '',
          duration_minutes: a.duration_minutes || 60,
          location_name: a.location_name || '',
          visibility_level: a.invited_group_id ? 'group_only' : (a.visibility_level || 'friend_circle'),
          invited_group_id: a.invited_group_id || '',
        });
      } catch (err) {
        setError('Failed to load activity');
      }
    };
    loadData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const dataToSubmit = {
        ...formData,
        duration_minutes: parseInt(formData.duration_minutes, 10),
        visibility_level: formData.visibility_level === 'group_only' ? 'private' : formData.visibility_level,
      };
      if (!dataToSubmit.start_time) delete dataToSubmit.start_time;
      if (!dataToSubmit.invited_group_id) delete dataToSubmit.invited_group_id;
      await activitiesAPI.update(id, dataToSubmit);
      navigate(`/activity/${id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update activity');
    } finally {
      setLoading(false);
    }
  };

  if (!formData) {
    return <div style={{ padding: 40, color: '#999' }}>Loading…</div>;
  }

  return (
    <div className="create-activity-container">
      <div className="create-activity-card">
        <h1>Edit Activity</h1>
        <p className="subtitle">Update the details below.</p>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Activity Type *</label>
            <select name="activity_type" value={formData.activity_type} onChange={handleChange} required>
              {activityTypes.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Easy morning run"
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Add any details..."
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>When</label>
              <input
                type="datetime-local"
                name="start_time"
                value={formData.start_time}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Duration (minutes)</label>
              <input
                type="number"
                name="duration_minutes"
                value={formData.duration_minutes}
                onChange={handleChange}
                min={10}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              name="location_name"
              value={formData.location_name}
              onChange={handleChange}
              placeholder="e.g., Crissy Field"
            />
          </div>

          <div className="form-group">
            <label>Who can see this?</label>
            <select name="visibility_level" value={formData.visibility_level} onChange={handleChange}>
              <option value="private">Only me</option>
              <option value="friend_circle">My friends</option>
              <option value="public">Everyone</option>
              {groups.length > 0 && <option value="group_only">A specific group</option>}
            </select>
          </div>

          {formData.visibility_level === 'group_only' && (
            <div className="form-group">
              <label>Which group?</label>
              <select name="invited_group_id" value={formData.invited_group_id} onChange={handleChange} required>
                <option value="">Select a group…</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => navigate(`/activity/${id}`)}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
